import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import BrandHeader from "@/components/BrandHeader";
import ScorePicker from "@/components/ScorePicker";
import { PARTS, CLOSING_REFLECTIONS } from "@/data/questions";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Steps: 0 = intro/details, 1..PARTS.length = each part, last = closing
export default function Questionnaire() {
  const navigate = useNavigate();
  const totalSteps = 1 + PARTS.length + 1; // details + parts + closing
  const [step, setStep] = useState(0);

  const [details, setDetails] = useState({
    full_name: "",
    email: "",
    role_applied: "Social Media & Marketing",
    location: "",
  });

  const [answers, setAnswers] = useState({}); // {qid: {response, self_score}}
  const [closing, setClosing] = useState({}); // {cid: text}
  const [submitting, setSubmitting] = useState(false);

  const progress = useMemo(() => Math.round(((step + 1) / totalSteps) * 100), [step, totalSteps]);

  const updateAnswer = (qid, patch) =>
    setAnswers((prev) => ({ ...prev, [qid]: { ...(prev[qid] || {}), ...patch } }));

  const canAdvanceFromDetails = () =>
    details.full_name.trim() && /.+@.+\..+/.test(details.email) && details.role_applied.trim();

  const validatePart = (partIdx) => {
    const p = PARTS[partIdx];
    for (const q of p.questions) {
      const a = answers[q.id];
      if (!a || !a.response || !a.response.trim() || !a.self_score) return q.id;
    }
    return null;
  };

  const validateClosing = () => {
    for (const c of CLOSING_REFLECTIONS) {
      if (!closing[c.id] || !closing[c.id].trim()) return c.id;
    }
    return null;
  };

  const next = () => {
    if (step === 0) {
      if (!canAdvanceFromDetails()) {
        toast.error("Please fill in your name, a valid email, and the role.");
        return;
      }
    } else if (step >= 1 && step <= PARTS.length) {
      const partIdx = step - 1;
      const missing = validatePart(partIdx);
      if (missing) {
        toast.error("Please answer every question and pick a score before continuing.");
        const el = document.getElementById(`q-${missing}`);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
        return;
      }
    }
    setStep((s) => Math.min(s + 1, totalSteps - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const back = () => {
    setStep((s) => Math.max(0, s - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submit = async () => {
    const missing = validateClosing();
    if (missing) {
      toast.error("Please complete the closing reflections.");
      return;
    }
    setSubmitting(true);
    const flatAnswers = [];
    for (const p of PARTS) {
      for (const q of p.questions) {
        const a = answers[q.id];
        flatAnswers.push({
          question_id: q.id,
          category: q.category,
          part: p.title,
          question: q.prompt,
          response: a.response,
          self_score: a.self_score,
        });
      }
    }
    try {
      const payload = {
        ...details,
        answers: flatAnswers,
        closing_reflections: CLOSING_REFLECTIONS.reduce((acc, c) => {
          acc[c.prompt] = closing[c.id] || "";
          return acc;
        }, {}),
      };
      const res = await axios.post(`${API}/submissions`, payload);
      toast.success("Submission saved. Analysing your responses…");
      navigate(`/done/${res.data.id}`, { state: { submission: res.data } });
    } catch (e) {
      console.error(e);
      toast.error("Something went wrong submitting. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      <BrandHeader />

      {/* Progress bar */}
      <div className="sticky top-[64px] z-20 bg-[var(--tt-bg)]/95 backdrop-blur border-b border-[var(--tt-border)]">
        <div className="max-w-3xl mx-auto px-6 sm:px-8 py-4">
          <div className="flex items-center justify-between text-xs uppercase tracking-[0.18em] text-[var(--tt-muted)] mb-2">
            <span data-testid="progress-label">
              {step === 0
                ? "Your details"
                : step <= PARTS.length
                ? `Part ${step} of ${PARTS.length} · ${PARTS[step - 1].title}`
                : "Closing Reflections"}
            </span>
            <span>{progress}%</span>
          </div>
          <div className="h-1.5 w-full bg-[var(--tt-surface-2)] rounded-full overflow-hidden">
            <div
              className="h-full bg-[var(--tt-secondary)] transition-all duration-500"
              style={{ width: `${progress}%` }}
              data-testid="progress-bar"
            />
          </div>
        </div>
      </div>

      <main className="max-w-3xl mx-auto px-6 sm:px-8 py-10 sm:py-14">
        {step === 0 && (
          <section data-testid="step-details">
            <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">
              First — who are we speaking with?
            </h2>
            <p className="mt-3 text-[var(--tt-muted)]">
              We'll keep this with your responses so we can follow up properly.
            </p>
            <div className="mt-8 grid sm:grid-cols-2 gap-5">
              <Field
                label="Full name"
                value={details.full_name}
                onChange={(v) => setDetails({ ...details, full_name: v })}
                testid="input-fullname"
                placeholder="Joy Dela Cruz"
              />
              <Field
                label="Email"
                value={details.email}
                onChange={(v) => setDetails({ ...details, email: v })}
                testid="input-email"
                type="email"
                placeholder="you@example.com"
              />
              <Field
                label="Role you're applying for"
                value={details.role_applied}
                onChange={(v) => setDetails({ ...details, role_applied: v })}
                testid="input-role"
              />
              <Field
                label="Where are you based?"
                value={details.location}
                onChange={(v) => setDetails({ ...details, location: v })}
                testid="input-location"
                placeholder="City, Country"
              />
            </div>
          </section>
        )}

        {step >= 1 && step <= PARTS.length && (
          <PartView
            part={PARTS[step - 1]}
            partIndex={step - 1}
            answers={answers}
            updateAnswer={updateAnswer}
          />
        )}

        {step === totalSteps - 1 && (
          <section data-testid="step-closing">
            <div className="text-xs uppercase tracking-[0.2em] text-[var(--tt-primary)] font-semibold mb-3">
              Closing Reflections
            </div>
            <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">
              Three last thoughts. No scores — just you talking.
            </h2>
            <div className="mt-10 space-y-10">
              {CLOSING_REFLECTIONS.map((c, i) => (
                <div key={c.id}>
                  <div className="text-xs uppercase tracking-[0.18em] text-[var(--tt-muted)] mb-2">
                    Reflection {i + 1}
                  </div>
                  <p className="font-display text-xl sm:text-2xl font-medium leading-snug text-[var(--tt-text)]">
                    {c.prompt}
                  </p>
                  <textarea
                    data-testid={`closing-${c.id}`}
                    value={closing[c.id] || ""}
                    onChange={(e) => setClosing({ ...closing, [c.id]: e.target.value })}
                    rows={4}
                    className="mt-4 w-full rounded-xl border border-[var(--tt-border)] bg-[var(--tt-surface)] px-4 py-3 text-base focus:outline-none focus:border-[var(--tt-primary)] focus:ring-2 focus:ring-[var(--tt-primary)]/20 transition"
                    placeholder="Take your time…"
                  />
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Navigation */}
        <div className="mt-14 flex items-center justify-between border-t border-[var(--tt-border)] pt-8">
          <button
            type="button"
            onClick={back}
            disabled={step === 0 || submitting}
            data-testid="back-btn"
            className="text-sm uppercase tracking-[0.2em] text-[var(--tt-muted)] hover:text-[var(--tt-text)] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            ← Back
          </button>

          {step < totalSteps - 1 ? (
            <button
              type="button"
              onClick={next}
              data-testid="next-btn"
              className="inline-flex items-center px-7 py-3.5 rounded-xl bg-[var(--tt-primary)] hover:bg-[var(--tt-primary-hover)] text-white font-medium tracking-wide transition-colors"
            >
              Continue →
            </button>
          ) : (
            <button
              type="button"
              onClick={submit}
              disabled={submitting}
              data-testid="submit-btn"
              className="inline-flex items-center px-7 py-3.5 rounded-xl bg-[var(--tt-secondary)] hover:bg-[var(--tt-text)] text-white font-medium tracking-wide transition-colors disabled:opacity-60"
            >
              {submitting ? "Analysing…" : "Submit & Analyse →"}
            </button>
          )}
        </div>
      </main>
    </div>
  );
}

function Field({ label, value, onChange, testid, type = "text", placeholder }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-[0.18em] text-[var(--tt-muted)]">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
        className="mt-2 w-full rounded-xl border border-[var(--tt-border)] bg-[var(--tt-surface)] px-4 py-3 text-base focus:outline-none focus:border-[var(--tt-primary)] focus:ring-2 focus:ring-[var(--tt-primary)]/20 transition"
      />
    </label>
  );
}

function PartView({ part, partIndex, answers, updateAnswer }) {
  return (
    <section data-testid={`step-part-${partIndex + 1}`}>
      <div className="text-xs uppercase tracking-[0.2em] text-[var(--tt-primary)] font-semibold mb-3">
        Part {partIndex + 1} · {part.questions.length} questions
      </div>
      <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">
        {part.title}
      </h2>
      <p className="mt-3 text-[var(--tt-muted)] leading-relaxed">{part.subtitle}</p>

      <div className="mt-10 space-y-12">
        {part.questions.map((q, i) => {
          const a = answers[q.id] || {};
          return (
            <div id={`q-${q.id}`} key={q.id} data-testid={`question-${q.id}`}>
              <div className="text-xs uppercase tracking-[0.18em] text-[var(--tt-muted)] mb-2">
                Q{i + 1} · {q.category}
              </div>
              <p className="font-display text-xl sm:text-2xl font-medium leading-snug text-[var(--tt-text)]">
                {q.prompt}
              </p>
              {q.helper && (
                <p className="mt-2 text-sm text-[var(--tt-muted)] italic">{q.helper}</p>
              )}
              <textarea
                value={a.response || ""}
                onChange={(e) => updateAnswer(q.id, { response: e.target.value })}
                rows={5}
                data-testid={`answer-${q.id}`}
                placeholder="Write as much or as little as feels right…"
                className="mt-4 w-full rounded-xl border border-[var(--tt-border)] bg-[var(--tt-surface)] px-4 py-3 text-base focus:outline-none focus:border-[var(--tt-primary)] focus:ring-2 focus:ring-[var(--tt-primary)]/20 transition"
              />
              <ScorePicker
                value={a.self_score}
                onChange={(n) => updateAnswer(q.id, { self_score: n })}
                testIdPrefix={q.id}
              />
            </div>
          );
        })}
      </div>
    </section>
  );
}
